import { useEffect, useState, useRef } from "react"
import { isMobile } from "react-device-detect"
import Button from "@/components/core/Button/Button"
import editIcon from "@/public/icons/edit.svg"
import Input from "@/components/core/Input/Input"
import TextArea from "@/components/core/TextArea/TextArea"
import { apim } from "@/constants/api"
import { getAccessToken, setBannerConfirmationText } from "@/utils/helper"
import OtpFields from "@/components/Shared/Otp/v1"
import { PHONE_REGEX } from "@/constants/index"
import { useDispatch, useSelector } from "react-redux"
import { validNumber, validText, validateEmail } from "@/utils/validate"
import { validateField } from "@/utils/helper"
import { apiBaseUrl } from "@/constants/index"
import { submitDealershipUrl } from "@/utils/constants"
import {
  selectAuthState,
  setAuthModalVisibility,
} from "@/store/features/authSlice"
import Autocomplete from "@/components/core/Autocomplete/Autocomplete"
import { checkNumberExists } from "@/utils/enquiry"
import {
  cancelEnquiryAnalytics,
  editMobileAnalytics,
  getOtpAnalytics,
  privacyStatementAnalytics,
  submitEnquiryAnalytics,
} from "@/components/DealershipEnquiry/analytics"
import _get from "lodash/get"
import { isBrandTransact } from "@/utils/index"

const DealershipEnquiry = props => {
  const { staticText, setConfirmationSuccess } = props
  const {
    authModal: { staticTextPopup } = {},
    isAuth,
    user,
  } = useSelector(selectAuthState)
  const [verifiedMobileNumber, setVerifiedMobileNumber] = useState(false)
  const [data, setData] = useState({})
  const [error, setError] = useState({})
  const [verifyOTP, setVerifyOTP] = useState(false)
  const [changeNumber, setChangeNumber] = useState(false)
  const [count, setCount] = useState(0)
  const [userDetails, setUserDetails] = useState(false)
  const dispatch = useDispatch()

  useEffect(() => {
    if (isAuth) {
      const number = localStorage.getItem("numberAlreadyExists")
      setVerifiedMobileNumber(true)
      localStorage.removeItem("numberAlreadyExists")
      let dataInitialValue = {
        firstName: _get(user, "firstName", ""),
        phone: _get(user, "login", ""),
        lastName: _get(user, "lastName", ""),
        email: _get(user, "sub", ""),
        alternativePhone: "",
      }
      setData(dataInitialValue)
      setError({
        ...error,
        phone: "",
      })
    }
  }, [isAuth, user, verifiedMobileNumber])

  const termsRef = useRef(null)
  useEffect(() => {
    // to add analytics for privacy statement link
    const elem = termsRef.current.querySelectorAll("a")
    if (elem[0]) {
      elem[0].addEventListener("click", () =>
        privacyStatementAnalytics(
          elem[0].innerText?.toLowerCase(),
          elem[0].getAttribute("href")
        )
      )
    }
  }, [])

  const handleChangeNumber = event => {
    const { value } = event.target
    if (value === "" || validNumber(value)) {
      setData({
        ...data,
        phone: value,
      })
      setError({
        ...error,
        phone: "",
      })
    }
  }

  const onChangeAlternatePhone = e => {
    const { value } = e.target
    const getValue = value.replace(/[^\d]/g, "")
    setData({
      ...data,
      alternativePhone: getValue,
    })
  }

  const onNameChange = e => {
    const { name, value } = e.target
    if (value === "" || validText(value)) {
      setData({
        ...data,
        [name]: value,
      })
      setError({ ...error, [name]: "" })
    }
  }

  const onChangeEmail = e => {
    const { value } = e.target
    setData({
      ...data,
      email: value,
    })
    if (value) {
      if (validateEmail(value)) {
        setError({
          ...error,
          email: null,
        })
      } else {
        setError({
          ...error,
          email: staticText.emailAddressError,
        })
      }
    }
  }

  const handleCityChange = value => {
    setData({
      ...data,
      city: value,
    })

    const cities = staticText.cities?.map(ele => ele.text)
    if (!value || value === "" || !cities.includes(value)) {
      setError({
        ...error,
        city: staticText.cityError,
      })
    } else
      setError({
        ...error,
        city: "",
      })
  }

  const handleSpaceAvailableOptionChange = value => {
    setData({
      ...data,
      spaceAvailableOption: value,
    })
    if (value === "") {
      setError({
        ...error,
        spaceAvailableOption: staticText.spaceAvailableError,
      })
    } else {
      setError({
        ...error,
        spaceAvailableOption: "",
      })
    }
  }

  const handleBudgetOptionChange = value => {
    setData({
      ...data,
      budgetOption: value,
    })
    if (value === "") {
      setError({
        ...error,
        budgetOption: staticText.budgetError,
      })
    } else {
      setError({
        ...error,
        budgetOption: "",
      })
    }
  }

  const isValidNumber = PHONE_REGEX.test(data?.phone)

  const onBlur = field => {
    setError({
      ...error,
      [field]: validateExist(field),
    })
  }

  const validateExist = field => {
    if (field === "firstName" || field === "lastName" || field === "company") {
      if (!data[field]?.trim() || !validateField("name", data[field])) {
        switch (field) {
          case "firstName":
            return staticText?.firstNameError
          case "lastName":
            return staticText?.lastNameError
          case "company":
            return staticText?.companyError
          default:
            return ""
        }
      }
    } else if (field === "phone") {
      if (!data?.phone?.trim() || !PHONE_REGEX.test(data?.phone)) {
        return staticText?.errorMobileNumber
      }
    } else if (field === "email") {
      if (!data[field]) {
        return staticText?.noEmailError
      } else if (
        !validateField("email", data[field]) ||
        !validateEmail(data[field])
      ) {
        return staticText?.emailAddressError
      } else {
        return ""
      }
    } else if (field === "spaceAvailableOption") {
      const spaceAvailableItems = staticText.spaceAvailableItems?.map(
        ele => ele.spaceAvailable
      )
      if (
        !data[field] ||
        data[field] === "" ||
        !spaceAvailableItems.includes(data[field])
      )
        return staticText.spaceAvailableError
    } else if (field === "budgetOption") {
      const budgetItems = staticText.budgetItems?.map(ele => ele.budget)
      if (
        !data[field] ||
        data[field] === "" ||
        !budgetItems.includes(data[field])
      )
        return staticText.budgetError
    } else if (field === "city") {
      const cities = staticText.cities?.map(ele => ele.text)
      if (!data[field] || data[field] === "" || !cities.includes(data[field]))
        return staticText.cityError
    }
  }

  const submitDealership = async payload => {
    let accessToken = null
    accessToken = getAccessToken()?.token
    return new Promise(async (resolve, reject) => {
      try {
        const { data: content } = await apim.post(
          `${apiBaseUrl}${submitDealershipUrl}`,
          payload
        )

        resolve(content)
      } catch (error) {
        reject(error)
      }
    })
  }

  const scrollToTop = () => {
    document
      .querySelector("body")
      .scrollIntoView({ top: 0, behavior: "smooth" })
  }

  const handleSubmit = async () => {
    const errorList = getErrorList()
    let errorDataList = []
    setError(errorList)
    for (const key in errorList) {
      errorDataList.push(errorList[key])
    }

    errorDataList.map((item, i) => {
      if (i === 0) document.getElementById(`${item}`)?.focus()
    })

    const isInvalid = Object?.values(errorList)?.find(el => el)
    if (!isInvalid) {
      const {
        firstName,
        lastName,
        email,
        company,
        spaceAvailableOption,
        budgetOption,
        city,
        dealershipDescription,
        phone,
      } = data
      const payload = {
        ...{
          firstName: firstName?.trim(),
          lastName: lastName?.trim(),
          email,
          company,
          spaceAvailable: spaceAvailableOption,
          budget: budgetOption,
          city,
          description: dealershipDescription ?? "",
          mobile: phone,
          enquiryType: "dealership-enquiry",
        },
      }

      const response = await submitDealership(payload)
      if (response.statusCode === 200) {
        submitEnquiryAnalytics(payload, "success")
        setConfirmationSuccess(true)
        setBannerConfirmationText(
          staticText.dealershipBannerText,
          "enquiry-form-banner"
        )
        scrollToTop()
      } else {
        submitEnquiryAnalytics(payload, "failure", "form submission failed")
        setConfirmationSuccess(false)
        scrollToTop()
      }
    } else {
      submitEnquiryAnalytics(payload, "failure", "form validation failed")
    }
  }
  const handleResetData = () => {
    setData({})
    setError({})
    setVerifiedMobileNumber(false)
    scrollToTop()
    cancelEnquiryAnalytics()
  }

  const getErrorList = () => {
    const errorList = {
      firstName: validateExist("firstName"),
      lastName: validateExist("lastName"),
      company: validateExist("company"),
      email: validateExist("email"),
      spaceAvailableOption: validateExist("spaceAvailableOption"),
      budgetOption: validateExist("budgetOption"),
      city: validateExist("city"),
    }
    return errorList
  }

  const isEnabled =
    Object?.values(getErrorList())?.find(el => el) || !verifiedMobileNumber
      ? true
      : false

  const changeNumberHandler = async () => {
    setVerifyOTP(true)
    setChangeNumber(true)
    editMobileAnalytics()
  }

  const getOTP = async () => {
    let { numberExists, factorId, userId } = await checkNumberExists(
      data?.phone
    )
    const isBrandOrTransact = isBrandTransact()
    if (!isBrandOrTransact && numberExists && !isAuth) {
      dispatch(setAuthModalVisibility({ show: true }))
    } else {
      if (isValidNumber) {
        setUserDetails({ factorId: factorId, userId: userId })
        setVerifyOTP(true)
        setChangeNumber(false)
      } else {
        setVerifyOTP(false)
      }
    }
    getOtpAnalytics()
  }

  const verifiedNumber = () => {
    setVerifiedMobileNumber(true)
    setVerifyOTP(false)
  }

  const setPhoneNumber = phone => {
    setData({
      ...data,
      phone: phone,
    })
  }

  return (
    <div className="dealership-enquiry__margin-div">
      <div className="dealership-enquiry__title">
        <h2 className="dealership-enquiry__question">
          {staticText.dealershipTitle}
        </h2>
        <span className="dealership-enquiry__required-fields">
          {staticText.dealershipFormDescription}
        </span>
      </div>
      {!verifiedMobileNumber && (
        <div className="dealership-enquiry__phoneDiv flex-mobile">
          <div className="dealership-enquiry__countryIcon country-code">
            <Input
              customClass=""
              id="contact-user-country-code"
              type="text"
              disabled
              value={"+91"}
            />
          </div>

          <div className="dealership-enquiry__phoneNumber phone-number">
            <Input
              disabled={verifiedMobileNumber}
              customClass="customer-support-inputfield"
              id="contact-user-phone"
              type="text"
              value={data.phone ?? ""}
              maxLength={10}
              placeholder={staticText?.mobileNumber}
              label={staticText?.mobileNumber}
              onChange={handleChangeNumber}
              showError={error?.phone}
              errorMessage={error?.phone}
              onBlur={() => onBlur("phone")}
            />
          </div>

          <button
            onClick={getOTP}
            style={
              isValidNumber ? { cursor: "pointer" } : { cursor: "not-allowed" }
            }
            className={`dealership-enquiry__getOTPLink ${
              isValidNumber && "--active"
            }`}
          >
            {staticText?.dealershipEnquiryGetOtpLabel}
          </button>
        </div>
      )}
      {verifiedMobileNumber && (
        <div>
          <Input
            customClass={
              isAuth
                ? "dealership-enquiry__phoneDivDisabled non-editable"
                : "dealership-enquiry__phoneDivDisabled"
            }
            type="text"
            disabled
            value={`+91 ${data?.phone}`}
            placeholder={staticText?.mobileNumber}
            label={staticText?.mobileNumber}
            suffix={
              verifiedMobileNumber && (
                <img
                  src={editIcon?.src}
                  alt=""
                  aria-hidden="true"
                  onClick={changeNumberHandler}
                  style={{ cursor: "pointer" }}
                />
              )
            }
          />
        </div>
      )}
      <div
        className={`${
          !verifiedMobileNumber && isValidNumber
            ? "dealership-enquiry__verifyNumberText --error"
            : "dealership-enquiry__verifyNumberText"
        }`}
      >
        {!verifiedMobileNumber
          ? staticText?.verifyMobile
          : staticText?.mobileVerified}
      </div>
      <div className="row mx-0">
        <div className="col-12 col-sm-6 col-md-6 pl-0 pr-10 m-pr-0">
          <Input
            id="contact-user-fname"
            type="text"
            customClass="customer-support-inputfield"
            value={data?.firstName ?? ""}
            maxLength={40}
            placeholder={staticText?.firstName}
            label={staticText?.firstName}
            onChange={onNameChange}
            onInput={onNameChange}
            showError={error?.firstName}
            errorMessage={error?.firstName}
            onBlur={() => onBlur("firstName")}
            name="firstName"
          />
        </div>
        <div className="col-12 col-sm-6 col-md-6 pl-10 pr-0 m-pl-0">
          <Input
            id="contact-user-lname"
            type="text"
            customClass="customer-support-inputfield"
            value={data?.lastName ?? ""}
            maxLength={40}
            placeholder={staticText?.lastName}
            label={staticText?.lastName}
            onChange={onNameChange}
            onInput={onNameChange}
            showError={error?.lastName}
            errorMessage={error?.lastName}
            onBlur={() => onBlur("lastName")}
            name="lastName"
          />
        </div>
      </div>
      <Input
        id="contact-user-email"
        type="text"
        placeholder={staticText?.emailAddress}
        label={staticText?.emailAddress}
        value={data?.email ?? ""}
        maxLength={60}
        autoComplete={true}
        onChange={onChangeEmail}
        onInput={onChangeEmail}
        showError={error?.email !== "" && error?.email}
        errorMessage={error?.email}
        onBlur={() => onBlur("email")}
      />

      <div className="dealership-enquiry__phoneDiv flex-mobile">
        <div className="dealership-enquiry__countryIcon country-code">
          <Input
            customClass=""
            id="contact-user-country-code"
            type="text"
            disabled
            value={"+91"}
          />
        </div>

        <div className="dealership-enquiry__alternativephoneNumber">
          <Input
            customClass="customer-support-inputfield"
            id="contact-user-alternativephone"
            type="text"
            value={data.alternativePhone}
            maxLength={10}
            placeholder={staticText?.alternateMobileNumber}
            label={staticText?.alternateMobileNumber}
            onChange={onChangeAlternatePhone}
          />
        </div>
      </div>

      <div className="mb-20">
        <Autocomplete
          label={staticText.city}
          data={staticText.cities?.map(ele => ({ name: ele.text }))}
          id="contact-user-city"
          name="city"
          value={data.city ?? ""}
          onChange={handleCityChange}
          onBlur={() => onBlur("city")}
          errorMessage={error?.city}
          showError={!error.city || error.city === "" ? false : true}
        />
      </div>

      <div className="mb-20">
        <Autocomplete
          label={staticText.spaceAvailable}
          data={staticText.spaceAvailableItems?.map(ele => ({
            name: ele.spaceAvailable,
          }))}
          id="contact-user-space-available"
          name="spaceAvailableOption"
          value={data.spaceAvailableOption ?? ""}
          onChange={handleSpaceAvailableOptionChange}
          showError={
            !error.spaceAvailableOption || error.spaceAvailableOption === ""
              ? false
              : true
          }
          errorMessage={error?.spaceAvailableOption}
          onBlur={() => onBlur("spaceAvailableOption")}
        />
      </div>
      <div className="mb-20">
        <Autocomplete
          label={staticText.budget}
          data={staticText.budgetItems?.map(ele => ({ name: ele.budget }))}
          id="contact-user-budget"
          name="budgetOption"
          value={data.budgetOption ?? ""}
          onChange={handleBudgetOptionChange}
          showError={
            !error.budgetOption || error.budgetOption === "" ? false : true
          }
          errorMessage={error?.budgetOption}
          onBlur={() => onBlur("budgetOption")}
        />
      </div>
      <Input
        id="contact-user-company-name"
        type="text"
        customClass="customer-support-inputfield"
        value={data?.company ?? ""}
        maxLength={40}
        placeholder={staticText?.company}
        label={staticText?.company}
        onChange={onNameChange}
        onInput={onNameChange}
        showError={error?.company}
        errorMessage={error?.company}
        onBlur={() => onBlur("company")}
        name="company"
      />
      <div className="dealership-enquiry__remarksDiv pt-25 pb-40 mt-30">
        <div className="dealership-enquiry__remark-note mb-25">
          {staticText.dealershipSubDescription}
        </div>
        <div className="dealership-enquiry__textarea_wrapper">
          <TextArea
            label={staticText?.describeYourRequest}
            placeholder={""}
            value={data?.dealershipDescription ?? ""}
            className="dealership-enquiry__textarea"
            maxLength="500"
            onChange={e => {
              const { value } = e.target
              setData({
                ...data,
                dealershipDescription: value,
              })
              setCount(e.target.value.length)
            }}
          />
          <p className="dealership-enquiry__textarea_placeholder">
            {count}/500
          </p>
        </div>
      </div>
      <div
        className="dealership-enquiry__remark-enquiry mb-30 mt-30"
        ref={termsRef}
      >
        {staticText.dealershipPrivacyPolicyText}
      </div>
      <div className="dealership-enquiry__buttonDiv">
        <Button
          type="black"
          role="button"
          label={staticText.submit}
          className="dealership-enquiry__button"
          flexible
          onClick={() => handleSubmit()}
          disabled={isEnabled}
        />
        <Button
          className="dealership-enquiry__button"
          type="ghost"
          role="button"
          label={staticText.cancel}
          flexible
          onClick={() => handleResetData()}
        />
      </div>
      {verifyOTP ? (
        <OtpFields
          handleClose={() => setVerifyOTP(false)}
          showModal={verifyOTP}
          staticTexts={staticText}
          mobile={data?.phone}
          verifiedNumber={verifiedNumber}
          changeMobileNumber={changeNumber}
          setDataEnquiry={phoneNumber => setPhoneNumber(phoneNumber)}
          userDetails={userDetails}
          page="dealership enquiry"
        />
      ) : null}
    </div>
  )
}
export default DealershipEnquiry
